import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ErreurApi, type CatalogueModes, type Permission, type ReglagesTuiles, type Session } from './api';
import { RUBRIQUES, CLES_RUBRIQUES, rubriqueParCle, type Rubrique } from '../../src/partage/rubriques.js';
import { grouperLeRail } from '../../src/partage/rail-groupes.js';
import { BasculeTheme } from './theme.js';
import { type SelectionCarte } from './carte';
import { CarteCadastrale } from './carte-cadastrale';
import { Connexion } from './connexion';
import { PanneauParcelle } from './parcelle';
import { AtelierSig } from './atelier';
import { PorteurDAnnonces } from './annonces';
// Le poste d'instruction : UNE affaire, instruite de bout en bout. Il ne
// double pas le Bureau, qui ne reçoit aucune affaire et montre la file.
import { PosteInstruction } from './poste-instruction';
import { Mutations } from './mutations';
// §7 — le verrou d'avant l'acte : qui peut signer, et à quelle date.
import { Capacite } from './capacite';
import { Successions } from './successions';
// §3 · §83 — le cockpit : ce qui bloque, et le geste suivant.
import { Bureau } from './bureau';
import { Anomalies } from './anomalies';
import { Prospective } from './prospective';
import { Intelligence } from './intelligence';
import { Dossiers } from './affaires';
import { Guichet } from './guichet';
import { Flux } from './flux';
import { Icone, type NomIcone } from './icones';
import { Geometre } from './geometre';
import { ImportSig } from './imports';
import { Cheminement } from './cogo';
import { Terrain } from './terrain';
import { ServicesOgc } from './ogc';
import { basculer, type EtatHorsLigne } from './hors-ligne';
import { Documents } from './documents';
import { Comptes } from './comptes';
import { Foncier } from './foncier';
import { BureauGeometre } from './bureau-geometre';
import { BureauMetier } from './bureau-metier';
import { Verifier } from './verifier';
import { Immobilier } from './immobilier';
import { Coproprietes } from './coproprietes';
import { Fiscal } from './fiscal';
import { Territoire } from './territoire';
import { Courrier } from './courrier';
import { Attestations } from './attestations';
import { Livre } from './livre';

/**
 * NAVIGATION (§58) ET MODES MÉTIER (§37 à §43).
 *
 * ⚠️ LE CATALOGUE DES RUBRIQUES A DÉMÉNAGÉ DANS `src/partage/rubriques.ts`.
 * Il était ici, et la base nomme désormais des rubriques (`sec.mode_rubrique`).
 * Deux listes — l'une en base, l'autre dans cet écran — auraient divergé au
 * premier ajout, et la divergence se serait manifestée par une entrée de rail
 * silencieusement absente : indiscernable d'un rail correct. C'est le même
 * raisonnement que pour `prefixes-api.ts`, dont les deux copies avaient
 * effectivement divergé.
 *
 * ⚠️ LE MODE COMPOSE LE RAIL ; IL N'OUVRE RIEN. Une rubrique du mode que
 * l'habilitation refuse reste affichée, désactivée, marquée « interdit » — et
 * c'est utile : elle dit à l'agent que son métier suppose cet écran et que son
 * habilitation ne le couvre pas. La cacher ferait croire qu'il n'existe pas.
 */

/** Une entrée du rail : une rubrique connue, ou une rubrique déclarée en base
 *  que cette interface ne sait pas dessiner (`inconnue`), ou une rubrique
 *  ouverte hors du rail du mode courant (`horsMode`). */
type EntreeRail = Rubrique & { inconnue?: boolean; horsMode?: boolean };

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [chargement, setChargement] = useState(true);
  const [catalogue, setCatalogue] = useState<CatalogueModes | null>(null);
  const [mode, setMode] = useState<string | null>(null);
  /** La parcelle désignée sur la carte, portée jusqu'au guichet (§2.3). */
  const [parcellePourDemande, setParcellePourDemande] = useState<string | null>(null);
  const [rubrique, setRubrique] = useState('carte');
  /**
   * ⚠️ L'ACTE QUE LE BUREAU DEMANDE D'OUVRIR. Il est remis à `null` dès que
   * l'on change de rubrique : sans cela, revenir sur « Mutations » plus tard
   * rouvrirait un acte qu'on n'a pas demandé — et l'agent croirait avoir
   * cliqué à côté.
   */
  const [acteOuvert, setActeOuvert] = useState<string | null>(null);
  useEffect(() => {
    // Toute rubrique AUTRE que « mutations » referme l'acte demandé par le
    // bureau. Le bureau, lui, pose les deux dans le même geste : l'effet voit
    // alors « mutations » et ne referme rien.
    if (rubrique !== 'mutations') setActeOuvert(null);
    // ⚠️ MÊME RÈGLE POUR L'INTENTION DU COCKPIT : quitter le guichet doit
    // l'oublier, sinon revenir au guichet par la barre latérale rouvrirait le
    // formulaire de la dernière opération cliquée — l'agent croirait avoir
    // lancé un dépôt qu'il n'a pas demandé.
    if (rubrique !== 'guichet') setOperation(null);
  }, [rubrique]);
  const [operation, setOperation] = useState<string | null>(null);
  /* ⚠️ L'état `selection` qui vivait ici ne nourrissait que la bulle-bati
     flottante, retirée (D.943) : la sélection s'affiche dans la barre d'état,
     tenue par `carte-cadastrale.tsx` où elle vit déjà. */
  const [reglages, setReglages] = useState<ReglagesTuiles | null>(null);
  const [recherche, setRecherche] = useState('');
  const [parcelleOuverte, setParcelleOuverte] = useState<string | null>(null);
  /**
   * La section de la parcelle ouverte au tiroir — exclusion D.995 : quand le
   * tiroir montre la planche d'une section, l'inspecteur de la carte se tait.
   * Portée ICI parce que l'app est le seul point où le tiroir et la carte se
   * rencontrent.
   */
  const [sectionDuTiroir, setSectionDuTiroir] = useState<{ code: string; circ: number } | null>(null);
  /**
   * Le dossier que l'on veut ouvrir, d'où qu'on le demande.
   *
   * ⚠️ IL VIT ICI ET NON DANS L'ÉCRAN DES DOSSIERS, parce que la demande vient
   * d'ailleurs : du panneau de parcelle, qui nomme le dossier de l’affaire.
   * Tant que cet état était privé à `dossiers.tsx`, le numéro de parcelle et le
   * numéro de dossier ouvraient deux surfaces sans passerelle entre elles.
   */
  const [dossierOuvert, setDossierOuvert] = useState<string | null>(null);
  /** Même motif que `dossierOuvert` : la demande vient d'un autre écran. */
  const [documentOuvert, setDocumentOuvert] = useState<string | null>(null);
  /** Même motif : la personne est demandée depuis la recherche du bureau. */
  const [personneOuverte, setPersonneOuverte] = useState<string | null>(null);

  /**
   * D.940 — l'état du fonctionnement hors réseau.
   *
   * ⚠️ UN BANDEAU QUI DIT « nouvelle version » DOIT ÊTRE VRAI, SINON IL SE
   * FAIT IGNORER. `main.tsx` ne l'émet qu'après avoir distingué la première
   * installation d'une vraie mise à jour : annoncer une nouveauté à la
   * première visite userait la confiance qu'on demandera le jour où il faudra
   * vraiment recharger.
   */
  const [horsLigne, setHorsLigne] = useState<EtatHorsLigne>('inactif');
  useEffect(() => {
    const ecoute = (e: Event) => setHorsLigne((e as CustomEvent<EtatHorsLigne>).detail);
    window.addEventListener('cadastral:hors-ligne', ecoute);
    return () => window.removeEventListener('cadastral:hors-ligne', ecoute);
  }, []);

  /**
   * Ouvre le dossier d'une affaire depuis n'importe où.
   *
   * ⚠️ ON FERME LE PANNEAU DE PARCELLE EN MÊME TEMPS. Sans cela, le tiroir
   * resterait posé PAR-DESSUS l'écran du dossier qu'on vient de demander :
   * l'agent croirait que son clic n'a rien fait, alors que la page est là,
   * dessous. Trois couches empilées pour une seul'affaire.
   */
  const ouvrirDossier = useCallback((reference: string) => {
    setParcelleOuverte(null);
    setDossierOuvert(reference);
    setRubrique('affaires');
  }, []);

  /**
   * Reprendre une saisie inachevée depuis le bureau.
   *
   * ⚠️ ON REPOSE LA PARCELLE, PAS SEULEMENT LE TYPE. La clé du brouillon est
   * `<type>.<parcelle ou 'sans-parcelle'>` : rouvrir le guichet sur le bon
   * type mais SANS la parcelle ferait chercher un brouillon sous une autre
   * clé, et le formulaire s'ouvrirait vierge — ce qui se lit « ma saisie a
   * disparu » alors qu'elle est là, sous la clé d'à côté.
   */
  const reprendreSaisie = useCallback((typeCode: string, parcelle: string | null) => {
    setParcellePourDemande(parcelle);
    setOperation(typeCode);
    setRubrique('guichet');
  }, []);

  const rafraichir = useCallback(async () => {
    setChargement(true);
    try {
      const s = await api.session.lire();
      setSession(s);
      if (s.ouverte) {
        setReglages(await api.reglagesTuiles().catch(() => null));
        // ⚠️ Le catalogue des modes n'est PAS bloquant. S'il échoue, on retombe
        // sur le rail complet — l'ancien comportement — plutôt que de refuser
        // l'accès au poste. Un mode est un confort ; le registre, non.
        const cat = await api.modes.catalogue().catch(() => null);
        setCatalogue(cat);
        /* ⚠️ LE MODE CHOISI SURVIT AU RECHARGEMENT, et seulement s'il est
           ENCORE le mien : une affectation qui change doit reprendre la main,
           sinon l'agent resterait sur le rail d'un métier qu'il n'a plus. */
        let garde: string | null = null;
        try { garde = window.localStorage.getItem('cadastral.mode'); } catch { /* stockage refusé */ }
        if (garde && cat?.modes.some((x) => x.code === garde)) {
          setMode(garde);
          const m = cat.modes.find((x) => x.code === garde);
          if (m?.rubriqueAtterrissage) setRubrique(m.rubriqueAtterrissage);
        } else if (cat?.modeParDefaut) {
          setMode(cat.modeParDefaut);
          const m = cat.modes.find((x) => x.code === cat.modeParDefaut);
          setRubrique(m?.rubriqueAtterrissage ?? 'carte');
        }
      }
    } catch (err) {
      // 401 = pas de session : ce n'est pas une erreur, c'est l'état initial.
      if (err instanceof ErreurApi && err.estNonAuthentifie) setSession({ ouverte: false });
      else setSession({ ouverte: false });
    } finally { setChargement(false); }
  }, []);

  useEffect(() => { void rafraichir(); }, [rafraichir]);

  const peut = useMemo(() => {
    const perms: Permission[] = session?.permissions ?? [];
    return (ressource: string, operation = 'lire') =>
      perms.some((p) => p.ressource === ressource && p.operation === operation);
  }, [session]);

  const onSelection = useCallback((s: SelectionCarte | null) => {
    if (s?.couche === 'parcelles' && s.identifiant) setParcelleOuverte(s.identifiant);
  }, []);

  const modeActif = catalogue?.modes.find((m) => m.code === mode) ?? null;

  /**
   * Le rail effectif. Sans mode — `citoyen`, `titulaire`, ou catalogue
   * injoignable — c'est la liste complète : on ne cache rien faute de savoir
   * quoi montrer.
   *
   * ⚠️ UNE RUBRIQUE DÉCLARÉE EN BASE ET INCONNUE ICI EST RENDUE COMME UNE
   * ERREUR, jamais sautée. Sauter la ligne produirait un rail amputé
   * strictement indiscernable d'un rail correct — et personne ne cherche ce
   * qu'il ne voit pas manquer.
   */
  const rail: EntreeRail[] = useMemo(() => {
    const cles = modeActif ? modeActif.rubriques : CLES_RUBRIQUES;
    const liste: EntreeRail[] = cles.map((cle): EntreeRail => {
      const r = rubriqueParCle(cle);
      return r ?? {
        cle, libelle: cle, icone: '⚠️', etat: 'non_construit' as const,
        manque: `Rubrique « ${cle} » déclarée dans sec.mode_rubrique et inconnue de cette interface.`,
        inconnue: true,
      };
    });
    // La rubrique ouverte hors du rail du mode (arrivée par une tuile, une
    // recherche) reste atteignable : sinon l'agent s'y trouve sans retour.
    if (!liste.some((r) => r.cle === rubrique)) {
      const r = rubriqueParCle(rubrique);
      if (r) liste.push({ ...r, horsMode: true });
    }
    return liste;
  }, [modeActif, rubrique]);

  if (chargement) return <div className="attente-plein">Chargement de la session…</div>;
  if (!session?.ouverte) return <Connexion onOuverte={() => void rafraichir()} />;

  const profil = session.profil!;
  const active = rail.find((r) => r.cle === rubrique) ?? rubriqueParCle(rubrique) ?? RUBRIQUES[0]!;

  return (
    /* ⚠️ UNE SEULE RÉGION D'ANNONCE pour tout le poste — D.949. Les composants
       appellent `useAnnoncer()`, aucun ne porte sa propre région live. */
    <PorteurDAnnonces>
    <div className="poste">
      <header className="barre">
        <div className="marque-compacte"><span className="sigle">MC</span> Mowibo Cadastral</div>
        <BasculeTheme />

        {/* ═══════════════════════════════════════════════════════════════
            ⚠️⚠️ LE MÉTIER S'AFFICHE, IL NE SE CHOISIT PAS.

            Ici se trouvait un `<select>` offrant les SEPT modes du catalogue.
            Son commentaire le justifiait ainsi : « adopter un mode ne donne
            rien, et l'agent doit pouvoir voir ce que son métier suppose ».
            C'est vrai des PERMISSIONS, et faux de tout le reste.

            Mesuré le 22/09/2026 : `GET /bureau/metier/:mode` servait n'importe
            quel mode à n'importe qui — DIALLO, agent de la Commune de Matoto,
            ouvrait le bureau du notaire, du géomètre et du fiscal, 200 partout.
            Un agent qui endosse le costume d'un autre métier croit que le
            cloisonnement est une préférence d'affichage.

            ⚠️ ET LE SÉLECTEUR MASQUAIT UNE CONTRADICTION : le mode dit par le
            RÔLE et celui dit par le SERVICE discordent pour 7 comptes sur 11.
            Tant qu'on laissait choisir, personne ne voyait que le système avait
            deux réponses. Le service l'emporte — c'est le dispatch qui le force,
            puisqu'il attribue les affaires par organisation.
            ═══════════════════════════════════════════════════════════════ */}
        {catalogue && catalogue.modes.length > 0 && (
          <div className="mon-metier" title={modeActif ? `${modeActif.section} — ${modeActif.resume}` : undefined}>
            <span className="aide">Métier</span>
            {/* ═══════════════════════════════════════════════════════════════
                ⚠️⚠️ LE CHOIX EST ROUVERT — MAIS SEULEMENT ENTRE MES MODES.
                ═══════════════════════════════════════════════════════════════
                Le sélecteur avait été retiré parce qu'il offrait LES SEPT
                métiers : un agent communal endossait le costume d'un notaire.
                Le catalogue est depuis borné à `codesMiens` — « on ne choisit
                pas son métier : on l'a » — et le danger décrit n'existe plus.

                ⚠️ CE QUE SON ABSENCE COÛTAIT, MESURÉ LE 24/09/2026. Une commune
                tient DEUX modes, `collectivite` et `urbanisme`. L'atterrissage
                prend le mieux classé : `urbanisme` (rang 50) devance
                `collectivite` (rang 60). Or le rail d'`urbanisme` ne porte NI
                `guichet` NI `affaires` — ils sont au rail de `collectivite`.
                Les 4 agents de commune du registre ne pouvaient donc atteindre
                ni le guichet de leur propre mairie, ni leurs affaires, et
                aucun écran ne disait pourquoi. La commune est pourtant le
                PREMIER service du circuit d'une donation.

                ⚠️ UN SEUL MODE : PAS DE BOUTON. Un sélecteur à une option
                laisse croire qu'il existe un ailleurs.
                ═══════════════════════════════════════════════════════════ */}
            {catalogue.modes.length > 1 ? (
              <span className="mon-metier-choix" role="group" aria-label="Mon métier">
                {catalogue.modes.map((m) => (
                  <button key={m.code} type="button" className="jeton-bascule"
                          aria-pressed={mode === m.code}
                          title={`${m.section} — ${m.resume}`}
                          onClick={() => {
                            setMode(m.code);
                            setRubrique(m.rubriqueAtterrissage ?? 'bureau');
                            try { window.localStorage.setItem('cadastral.mode', m.code); } catch { /* stockage refusé */ }
                          }}>
                    {m.libelle}
                  </button>
                ))}
              </span>
            ) : (
              <strong>{catalogue.modes[0]!.libelle}</strong>
            )}
            <span className="aide">
              {catalogue.origineDuMode === 'service'
                ? 'selon votre affectation'
                : 'selon vos rôles — aucun service ne vous est rattaché'}
            </span>
          </div>
        )}
        {catalogue?.discordance && (
          /* ⚠️ La discordance se DIT. La taire laisserait l'agent croire que
             son écran se trompe, alors qu'il est juste et que c'est sa fiche
             qui est incohérente. */
          <p className="metier-discordance">{catalogue.discordance}</p>
        )}

        <form className="recherche" onSubmit={(e) => { e.preventDefault(); if (recherche.trim()) setParcelleOuverte(recherche.trim()); }}>
          <input
            value={recherche} onChange={(e) => setRecherche(e.target.value)}
            placeholder="Référence (003-AAK-0326), réf. scellée ou identifiant interne" aria-label="Rechercher une parcelle"
          />
          <button className="bouton">Ouvrir</button>
        </form>

        <div className="identite">
          <div>
            <strong>{profil.nomAffichage ?? profil.compteId}</strong>
            <span>{profil.roles.join(', ')} · {profil.organisation ?? 'sans organisation'}</span>
          </div>
          <span className="jeton" title={profil.porteeNationale ? 'Portée nationale' : `${profil.porteesDivisionIds.length} division(s)`}>
            {profil.territoire ?? '??'} · {profil.porteeNationale ? 'national' : `${profil.porteesDivisionIds.length} div.`}
          </span>
          <button className="bouton discret" onClick={async () => { await api.session.fermer(); void rafraichir(); }}>Fermer</button>
        </div>
      </header>

      <div className="corps">
        {horsLigne === 'mise-a-jour-en-attente' && (
          <div className="maj-bandeau">
            <span>Une nouvelle version de l’atelier est prête.</span>
            <button onClick={basculer}>Recharger</button>
            <em>Terminez votre saisie d’abord : le rechargement remplace la page.</em>
          </div>
        )}
        <nav className="rail">
          {/* ⚠️ RAIL À DEUX NIVEAUX — demande du fondateur, 21/09/2026.
              Vingt-cinq rubriques dans une liste plate ne se parcourent pas : on
              les lit TOUTES pour en trouver une. Les groupes sont contigus dans
              l'ordre existant — l'ordre du rail porte déjà une règle (§7), on
              ajoute des intertitres, on ne déplace rien.
              Un groupe sans entrée visible n'est pas rendu : un intertitre seul
              annoncerait un contenu que ce mode n'ouvre pas. */}
          {grouperLeRail(rail).map((groupe) => (
            <div key={groupe.titre} className="rail-groupe">
              <span className="rail-groupe-titre">{groupe.titre}</span>
              {groupe.entrees.map((r) => {
                /* ⚠️ D.968 : « lire » ne sait pas dire « servie aux seuls
                   traceurs ». Une rubrique qui déclare ses opérations est
                   active si l'agent en porte AU MOINS UNE — ce sont celles
                   que les routes du service exigent. */
                const interdit = r.ressource
                  ? (r.operations && r.operations.length > 0
                      ? !r.operations.some((op) => peut(r.ressource!, op))
                      : !peut(r.ressource))
                  : false;
                const inactif = r.etat === 'non_construit' || interdit;
                return (
                  <button
                    key={r.cle}
                    className={`rail-item ${rubrique === r.cle ? 'actif' : ''} ${inactif ? 'inactif' : ''}`}
                    disabled={inactif}
                    onClick={() => setRubrique(r.cle)}
                    title={interdit ? `Votre rôle n'a aucune des permissions ${r.ressource}:${(r.operations ?? ['lire']).join('|')}`
                                    : r.etat === 'non_construit' ? `Non construit — ${r.manque}` : r.libelle}
                  >
                    <Icone nom={r.icone as NomIcone} taille={17} />
                    <em>{r.libelle}</em>
                    {/* ⚠️ Le motif du refus EXISTE dans l'arbre — refus D.951 :
                        une information portée par le seul `title` n'existe ni
                        au clavier ni au lecteur d'écran. */}
                    {interdit && (
                      <span className="visuellement-cache">
                        — refusé : aucune des permissions {r.ressource}:{(r.operations ?? ['lire']).join('|')}
                      </span>
                    )}
                    {r.horsMode && <b className="pastille">hors mode</b>}
                    {r.etat === 'non_construit' && !r.horsMode && <b className="pastille">à bâtir</b>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <main className="scene">
          {rubrique === 'carte' && (
            <>
              {/* ⚠️ UN SEUL ÉCRAN DEPUIS LE 22/09/2026. « Plan cadastral »
                  était une rubrique séparée qui redessinait les mêmes sections
                  au `<canvas>` : son tableau d'assemblage, ses feuilles et ses
                  deux impressions sont devenus les panneaux de CETTE carte.
                  Voir `carte-cadastrale.tsx`. */}
              <CarteCadastrale
              onSelection={onSelection}
              parcelleActive={parcelleOuverte}
              sectionDuTiroir={parcelleOuverte ? sectionDuTiroir : null}
              onOuvrirParcelle={setParcelleOuverte}
              reglages={reglages}
              /* L'ATELIER ne s'offre qu'à qui peut déposer : ce sont les deux
                 permissions que le service exige lui-même. Ici elles n'ouvrent
                 plus un montage : elles ouvrent l'affordance vers la page. */
              peutTracer={peut('cad.parcelle', 'creer') || peut('cad.parcelle', 'modifier')}
              onOuvrirAtelier={(p) => { setParcellePourDemande(p); setRubrique('atelier'); }}
            />
              {/* ⚠️ La bulle flottante du bâtiment a été retirée (D.943) :
                  la barre d'état porte la même information — sélection et
                  rattachement — dans une région dockée et stable. */}
            </>
          )}

          {rubrique === 'atelier' && (peut('cad.parcelle', 'creer') || peut('cad.parcelle', 'modifier')) && (
            /* ⚠️ L'ATELIER EST LA PAGE DU MÉTIER (D.968) : le rail la sert aux
               seuls porteurs de cad.parcelle:creer|modifier, et la parcelle
               voyage avec la rubrique comme pour le Guichet — consommée une
               fois, jamais un routeur. */
            <AtelierSig
              reglages={reglages}
              parcelleInitiale={parcellePourDemande}
              onParcelleConsommee={() => setParcellePourDemande(null)}
              onOuvrirParcelle={setParcelleOuverte}
              peutAppliquer={peut('cad.parcelle', 'modifier')}
              peutProposer={peut('cad.releve', 'creer')}
            />
          )}

          {rubrique === 'flux' && <Flux />}

          {rubrique === 'guichet' && (
            <Guichet
              onOuvrirDossier={() => setRubrique('affaires')}
              ouvrirType={operation}
              parcelleDesignee={parcellePourDemande}
              onParcelleConsommee={() => setParcellePourDemande(null)}
            />
          )}

          {rubrique === 'instruction' && (
            dossierOuvert
              ? <PosteInstruction reference={dossierOuvert}
                                  onFermer={() => setRubrique('bureau')} />
              : (
                /* ⚠️ PAS UN ÉCRAN VIDE. Le poste instruit UNE affaire ; sans
                   affaire ouverte il n'a rien à montrer, et un panneau muet se
                   lirait « le poste est cassé » au lieu de « choisissez une
                   affaire ». Il dit donc où aller, et le bouton y mène. */
                <section className="avertissement">
                  <strong>Aucune affaire n'est ouverte.</strong>
                  <p className="mention">
                    Le poste d'instruction travaille sur une seule affaire à la fois.
                    Choisissez-en une dans votre plan de travail, ou dans la file.
                  </p>
                  <button type="button" onClick={() => setRubrique('bureau')}>
                    Ouvrir mon bureau
                  </button>
                </section>
              )
          )}

          {rubrique === 'affaires' && (
            <Dossiers
              peutOuvrir={peut('fon.affaire', 'creer')}
              peutClore={peut('fon.affaire', 'modifier')}
              peutDeposerActe={peut('fon.mutation', 'creer')}
              referenceInitiale={dossierOuvert}
              permissionsBien={{
                // ⚠️ LES MÊMES APPELS `peut()` QUE LE TIROIR, et c'est la
                // condition pour que l'affaire se lise pareil des deux côtés.
                // Deux jeux de permissions calculés séparément finiraient par
                // diverger, et le même agent verrait un bouton ici et pas là.
                peutActiver: peut('cad.parcelle', 'modifier'),
                peutRattacherFiscal: peut('lien.objet_fiscal', 'creer'),
                peutValider: peut('fon.mutation', 'valider'),
                peutTraiterAnomalie: peut('cad.controle', 'traiter'),
              }}
              onOuvrirParcelle={setParcelleOuverte}
              onOuvrirMutation={() => setRubrique('mutations')}
            />
          )}

          {/* ⚠️ UN BUREAU PAR MÉTIER — règle du fondateur, 21/09/2026 : « chaque
              mode est un métier avec ses propres exigences, ses propres flux,
              ses propres attentes ; chacun sera un OS. »

              ⚠️ ET CE N'EST PAS UN AIGUILLAGE COSMÉTIQUE. Le notaire est
              organisé par ACTE — ce qu'il doit faire signer. Le géomètre l'est
              par CE QUE CHAQUE LEVÉ ATTEND : une anomalie ouverte attend LUI,
              un levé déposé attend QUELQU'UN D'AUTRE. Ce ne sont pas deux
              présentations d'une même liste, ce sont deux questions.

              Le repli sur `BureauMetier` n'est PAS un défaut : il donne à un
              métier ses compteurs et ses gestes propres en attendant que son
              corps de métier soit écrit — et il le DIT quand aucun geste n'est
              encore déclaré, au lieu d'afficher une rangée vide. */}
          {/* ⚠️⚠️ TANT QUE LE MÉTIER N'EST PAS CONNU, ON N'AFFICHE AUCUN BUREAU.
              La version précédente retombait sur celui du notaire quand
              `mode` valait `null` — c'est-à-dire pendant TOUT le chargement.
              Un géomètre voyait donc, une fraction de seconde, « 7 dossiers
              ouverts · 4 actes à signer » : les compteurs d'un métier qui
              n'est pas le sien.

              ⚠️ ET CE N'ÉTAIT PAS QU'UN CLIGNOTEMENT. Le composant se MONTE,
              donc il APPELLE `/bureau` — la route du cockpit notarial — pour
              un appelant qui n'est pas notaire. Le service cloisonne, il ne
              rendrait rien d'étranger ; mais on interroge une surface qui
              n'est pas celle du métier, et un écran qui se trompe de route se
              trompera un jour de contenu.

              Le fondateur l'a vu en une phrase : « pourquoi c'est le même
              bureau pour tout le monde ? » */}
          {rubrique === 'bureau' && mode === null && (
            <div className="attente-plein">Lecture de votre métier…</div>
          )}
          {rubrique === 'bureau' && mode === 'geometre' && (
            <BureauGeometre modeCode={mode} onOuvrirRubrique={setRubrique} onOuvrirAffaire={ouvrirDossier}
                            onReprendreSaisie={reprendreSaisie} />
          )}
          {rubrique === 'bureau' && mode !== null && mode !== 'notaire' && mode !== 'geometre' && (
            <BureauMetier modeCode={mode} onOuvrirRubrique={setRubrique} onOuvrirAffaire={ouvrirDossier}
                          onReprendreSaisie={reprendreSaisie} />
          )}
          {rubrique === 'bureau' && mode === 'notaire' && (
            <Bureau
              modeCode={mode}
              onOuvrirActe={(r) => { setRubrique('mutations'); setActeOuvert(r); }}
              onOuvrirParcelle={setParcelleOuverte}
              /* ⚠️ `ouvrirDossier` ferme le tiroir de parcelle EN MÊME TEMPS.
                 Sans cela le panneau resterait posé par-dessus l'affaire
                 demandée, et l'agent croirait son clic sans effet. */
              onOuvrirAffaire={ouvrirDossier}
              onReprendreSaisie={reprendreSaisie}
              onOuvrirDocument={(d) => { setRubrique('documents'); setDocumentOuvert(d); }}
              onOuvrirPersonne={(t) => { setRubrique('foncier'); setPersonneOuverte(t); }}
              onOuvrirRubrique={setRubrique}
              /**
               * ⚠️ UN CALLBACK DÉDIÉ, ET NON UN SECOND ARGUMENT SUR
               * `onOuvrirRubrique`. Celui-ci est `setRubrique` — un poseur
               * d'état React. Lui passer un deuxième argument ne lèverait
               * aucune erreur : il serait simplement avalé, et le type resterait
               * perdu exactement comme avant. Un défaut silencieux qui a
               * l'apparence d'un correctif.
               */
              onOuvrirOperation={(cle) => {
                if (cle.startsWith('demande:')) { setOperation(cle.slice(8)); setRubrique('guichet'); return; }
                setOperation(null);
                setRubrique(cle === 'succession' ? 'successions'
                  : cle === 'procuration' ? 'capacite'
                  : cle === 'acte' ? 'mutations' : 'affaires');
              }}
            />
          )}
          {rubrique === 'capacite' && <Capacite />}
          {rubrique === 'successions' && <Successions />}
          {rubrique === 'mutations' && <Mutations peutValider={peut('fon.mutation', 'valider')} onOuvrirParcelle={setParcelleOuverte} ouvrir={acteOuvert} />}

          {rubrique === 'documents' && <Documents peutDeposer={peut('doc.document', 'creer')} peutRevoquer={peut('doc.document', 'modifier')} identifiantInitial={documentOuvert} />}

          {rubrique === 'geometre' && <Geometre peutDecider={peut('cad.releve', 'valider')} onOuvrirParcelle={setParcelleOuverte} />}
          {/* D.926 — le géomètre dépose son fichier. La route existait et
              aucun écran ne l'appelait ; c'est ce que cette ligne répare. */}
          {rubrique === 'imports' && <ImportSig onOuvrirParcelle={setParcelleOuverte} />}
          {/* D.927 — reprendre un titre ancien : gisements et distances. */}
          {rubrique === 'cogo' && <Cheminement />}
          {/* D.930 — la saisie hors réseau, et ce qui n'est PAS entré. */}
          {rubrique === 'terrain' && <Terrain />}
          {rubrique === 'ogc' && <ServicesOgc />}

          {/* §19 — la file d'anomalies. `peutTraiter` gouverne l'inscription
              d'une décision ; la lecture est gouvernée par le service, qui
              refuse et le DIT. */}
          {rubrique === 'anomalies' && (
            <Anomalies peutTraiter={peut('cad.controle', 'traiter')} onOuvrirParcelle={setParcelleOuverte} />
          )}

          {rubrique === 'territoire' && <Territoire />}

          {/* §20-§25 — le terrain et ses contraintes. Deux étapes sur onze :
              l'écran le dit lui-même, en tête, plutôt que de laisser croire
              que le moteur de génération existe. */}
          {rubrique === 'prospective' && <Prospective />}
          {/* §19 · §31-§36 · §37 — l'écran DIT ce qu'il ne sait pas avant de noter. */}
          {rubrique === 'investment' && <Intelligence />}

          {/* §16 — la MÊME page que le public, dans la palette de l'atelier.
              Deux implémentations de la vérification divergeraient, et c'est
              précisément la surface où un écart se paierait le plus cher. */}
          {rubrique === 'verify' && <Verifier dansLAtelier />}

          {rubrique === 'comptes' && <Comptes peutAdministrer={peut('sec.habilitation', 'creer')} />}

          {/* §10-§11 — le marché. §12 — les copropriétés. §13 — l'assiette. */}
          {rubrique === 'immobilier' && <Immobilier onOuvrirParcelle={setParcelleOuverte} />}

          {rubrique === 'coproprietes' && <Coproprietes onOuvrirParcelle={setParcelleOuverte} />}

          {rubrique === 'fiscal' && <Fiscal onOuvrirParcelle={setParcelleOuverte} />}

          {/* D.432 — Courrier recoit le PROFIL : `compteId` est l'identifiant de
              l'agent sur le rail. Le composer dans l'ecran a partir d'autre chose
              ouvrirait une session au nom de quelqu'un d'autre. */}
          {rubrique === 'courrier' && <Courrier profil={profil} />}

          {/* LOT B — l'acte communal. LOT C — le registre du titre. Deux
              autorités distinctes (Art.3) : la commune atteste, la conservation
              immatricule. Le service tranche par `fon.autorite_fonciere` ;
              l'écran n'en récite rien. */}
          {rubrique === 'attestations' && <Attestations onOuvrirParcelle={setParcelleOuverte} />}

          {rubrique === 'livre' && <Livre onOuvrirParcelle={setParcelleOuverte} />}

          {rubrique === 'foncier' && (
            <Foncier
              parcelleInitiale={parcelleOuverte}
              titulaireInitial={personneOuverte}
              peutGreverr={peut('fon.restriction', 'creer')}
              peutInscrire={peut('fon.titulaire', 'creer')}
              onOuvrirParcelle={setParcelleOuverte}
            />
          )}

          {active.etat === 'non_construit' && (
            <div className="a-batir">
              <h2>{active.icone} {active.libelle}</h2>
              <p className="etiquette">Non construit</p>
              <p>{active.manque}</p>
              <p className="mention">
                Cette rubrique est affichée parce qu'elle figure au cahier des charges,
                et désactivée parce qu'elle n'existe pas. Un écran vide qui se présenterait
                comme fonctionnel serait indiscernable d'un module en panne.
              </p>
            </div>
          )}
        </main>

        {parcelleOuverte && (
          <PanneauParcelle
            identifiant={parcelleOuverte}
            onFermer={() => setParcelleOuverte(null)}
            onSection={setSectionDuTiroir}
            onOuvrirDossier={ouvrirDossier}
            /* ⚠️ LE MÉTIER ET LES PERMISSIONS, PAS UN BOOLÉEN PAR ACTION.
               Chaque nouvelle action ajoutait jusqu'ici un fil depuis ici —
               c'est pourquoi il n'y avait pas d'onglet Actions : le coût de
               l'ajout était dans le composeur, pas dans l'écran. Le panneau
               reçoit désormais de quoi DÉCIDER, et la table d'actions dit quoi. */
            metier={mode}
            /* ⚠️ Une action offerte OUVRE sa rubrique. Sans ce fil, le bouton
               existerait et ne ferait rien — « un bouton mal câblé ne lève pas,
               il navigue », et celui-ci ne navigerait même pas. */
            /* ⚠️⚠️ LA PARCELLE VOYAGE AVEC LA RUBRIQUE — §2.3. Ce fil laissait
               tomber l'identifiant : l'agent arrivait au guichet devant un
               formulaire vide et retapait une adresse que le registre connaît.
               Mesuré le 23/09/2026 : un dépôt a déclaré « Conakry / Matoto /
               Tombola » pour un fonds de Boké-Centre, et l'affaire est partie à
               250 km — c'est la RÉFÉRENCE (AFF-MTO-…) qui l'a montré, pas un
               contrôle. */
            onOuvrirRubrique={(r: string, parcelle?: string) => {
              setParcellePourDemande(parcelle ?? null);
              setRubrique(r);
              setParcelleOuverte(null);
            }}
            permissions={session?.permissions ?? []}
            peutActiver={peut('cad.parcelle', 'modifier')}
            peutReferencer={peut('cad.parcelle', 'modifier')}
            peutRattacherFiscal={peut('lien.objet_fiscal', 'creer')}
            peutValider={peut('fon.mutation', 'valider')}
            peutTraiterAnomalie={peut('cad.controle', 'traiter')}
          />
        )}
      </div>
    </div>
    </PorteurDAnnonces>
  );
}
